import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  FunnelChart, Funnel, Treemap, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

interface Campaign {
  _id: string
  subject: string
  sentCount: number
  openCount: number
  clickCount: number
  sentAt: string
}

interface AnalyticsEvent {
  campaignId: string
  subscriberEmail: string
  eventType: 'open' | 'click'
  timestamp: string
  userAgent?: string
  device?: string
  location?: string
  emailClient?: string
}

interface AnalyticsOverviewTabProps {
  campaigns: Campaign[]
  analytics: AnalyticsEvent[]
  metrics: {
    totalSent: number
    totalOpened: number
    totalClicked: number
    avgOpenRate: string
    avgClickRate: string
    avgCTR: string
    engagementScore: number
  }
}

export default function AnalyticsOverviewTab({ campaigns, analytics, metrics }: AnalyticsOverviewTabProps) {
  
  const getCohortData = () => {
    const cohorts: Record<string, Record<string, number>> = {}
    
    campaigns.forEach(campaign => {
      const cohortMonth = new Date(campaign.sentAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      if (!cohorts[cohortMonth]) {
        cohorts[cohortMonth] = { sent: 0, opened: 0, clicked: 0 }
      }
      cohorts[cohortMonth].sent += campaign.sentCount
      cohorts[cohortMonth].opened += campaign.openCount
      cohorts[cohortMonth].clicked += campaign.clickCount
    })

    return Object.entries(cohorts).map(([month, data]) => ({
      month,
      sent: data.sent,
      opened: data.opened,
      clicked: data.clicked,
      openRate: data.sent > 0 ? ((data.opened / data.sent) * 100).toFixed(1) : 0,
      clickRate: data.opened > 0 ? ((data.clicked / data.opened) * 100).toFixed(1) : 0,
      retention: data.sent > 0 ? ((data.clicked / data.sent) * 100).toFixed(1) : 0
    }))
  }

  const getForecastData = () => {
    const historical = campaigns.slice(0, 12).reverse()
    const avgGrowth = historical.length > 1 
      ? historical.reduce((sum, c, i) => {
          if (i === 0) return 0
          return sum + ((c.sentCount - historical[i - 1].sentCount) / historical[i - 1].sentCount)
        }, 0) / (historical.length - 1)
      : 0.05

    const lastSent = historical[historical.length - 1]?.sentCount || 1000
    const forecast = []
    
    for (let i = 1; i <= 6; i++) {
      const projected = Math.round(lastSent * Math.pow(1 + avgGrowth, i))
      forecast.push({
        period: `+${i}mo`,
        type: 'forecast',
        sent: projected,
        openEstimate: Math.round(projected * 0.25),
        clickEstimate: Math.round(projected * 0.05)
      })
    }

    return [
      ...historical.map(c => ({
        period: new Date(c.sentAt).toLocaleDateString('en-US', { month: 'short' }),
        type: 'actual',
        sent: c.sentCount,
        openEstimate: c.openCount,
        clickEstimate: c.clickCount
      })),
      ...forecast
    ]
  }

  const getGeographicData = () => {
    const locationMap: Record<string, { opens: number; clicks: number }> = {}
    
    analytics.forEach(event => {
      const loc = event.location || 'Unknown'
      if (!locationMap[loc]) {
        locationMap[loc] = { opens: 0, clicks: 0 }
      }
      if (event.eventType === 'open') locationMap[loc].opens++
      if (event.eventType === 'click') locationMap[loc].clicks++
    })

    return Object.entries(locationMap)
      .map(([location, data]) => ({
        location,
        opens: data.opens,
        clicks: data.clicks,
        ctr: data.opens > 0 ? ((data.clicks / data.opens) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.opens - a.opens)
      .slice(0, 10)
  }

  const getHeatmapData = () => {
    const heatmap: Record<string, Record<string, number>> = {}
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const hours = Array.from({ length: 24 }, (_, i) => i)

    days.forEach(day => {
      heatmap[day] = {}
      hours.forEach(hour => {
        heatmap[day][hour] = 0
      })
    })

    analytics.filter(e => e.eventType === 'open').forEach(event => {
      const date = new Date(event.timestamp)
      const day = days[date.getDay()]
      const hour = date.getHours()
      heatmap[day][hour]++
    })

    return days.flatMap(day => 
      hours.map(hour => ({
        day,
        hour,
        value: heatmap[day][hour]
      }))
    ).filter(d => d.value > 0)
  }

  const getFunnelData = () => {
    const totalSent = campaigns.reduce((sum, c) => sum + c.sentCount, 0)
    const totalOpened = campaigns.reduce((sum, c) => sum + c.openCount, 0)
    const totalClicked = campaigns.reduce((sum, c) => sum + c.clickCount, 0)
    const estimatedConversions = Math.round(totalClicked * 0.15)

    return [
      { stage: 'Sent', value: totalSent, fill: '#3b82f6' },
      { stage: 'Opened', value: totalOpened, fill: '#10b981' },
      { stage: 'Clicked', value: totalClicked, fill: '#f59e0b' },
      { stage: 'Converted', value: estimatedConversions, fill: '#ef4444' }
    ]
  }

  const getEmailClientData = () => {
    const clients: Record<string, number> = {}
    
    analytics.forEach(event => {
      const client = event.emailClient || 'Unknown'
      clients[client] = (clients[client] || 0) + 1
    })

    return Object.entries(clients)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }

  const getLifecycleData = () => {
    return [
      { stage: 'New', count: 450, percentage: 30 },
      { stage: 'Active', count: 600, percentage: 40 },
      { stage: 'Engaged', count: 225, percentage: 15 },
      { stage: 'At Risk', count: 150, percentage: 10 },
      { stage: 'Inactive', count: 75, percentage: 5 }
    ]
  }

  const getABTestData = () => {
    return [
      {
        variant: 'Control',
        sent: 1000,
        opened: 250,
        clicked: 50,
        openRate: 25,
        clickRate: 5,
        revenue: 500
      },
      {
        variant: 'Variant A',
        sent: 1000,
        opened: 320,
        clicked: 72,
        openRate: 32,
        clickRate: 7.2,
        revenue: 720
      },
      {
        variant: 'Variant B',
        sent: 1000,
        opened: 280,
        clicked: 61,
        openRate: 28,
        clickRate: 6.1,
        revenue: 610
      }
    ]
  }

  return (
    <>
      {/* Key Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-600 mb-1">Total Sent</p>
          <p className="text-3xl font-bold text-gray-900">{metrics.totalSent.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-600 mb-1">Avg Open Rate</p>
          <p className="text-3xl font-bold text-green-600">{metrics.avgOpenRate}%</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-600 mb-1">Avg Click Rate</p>
          <p className="text-3xl font-bold text-blue-600">{metrics.avgClickRate}%</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-sm text-gray-600 mb-1">Engagement Score</p>
          <p className="text-3xl font-bold text-purple-600">{metrics.engagementScore}/100</p>
        </div>
      </div>

      {/* Engagement Funnel */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Engagement Funnel</h2>
        <ResponsiveContainer width="100%" height={300}>
          <FunnelChart>
            <Tooltip />
            <Funnel
              dataKey="value"
              data={getFunnelData()}
              isAnimationActive
            >
              {getFunnelData().map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>

      {/* Cohort Analysis */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Cohort Retention Analysis</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={getCohortData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="sent" stackId="1" stroke="#3b82f6" fill="#3b82f6" />
            <Area type="monotone" dataKey="opened" stackId="2" stroke="#10b981" fill="#10b981" />
            <Area type="monotone" dataKey="clicked" stackId="3" stroke="#f59e0b" fill="#f59e0b" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Predictive Forecasting */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Predictive Growth Forecast</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={getForecastData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="sent" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
            <Line 
              type="monotone" 
              dataKey="openEstimate" 
              stroke="#10b981" 
              strokeWidth={2} 
              strokeDasharray="5 5"
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="clickEstimate" 
              stroke="#f59e0b" 
              strokeWidth={2}
              strokeDasharray="5 5" 
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-sm text-gray-500 mt-2">Dashed lines indicate projected values based on historical trends</p>
      </div>

      {/* Geographic Segmentation */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Geographic Performance</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getGeographicData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="location" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="opens" fill="#10b981" />
            <Bar dataKey="clicks" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Send Time Heatmap */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Optimal Send Time Heatmap</h2>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" type="number" domain={[0, 23]} label={{ value: 'Hour of Day', position: 'bottom' }} />
            <YAxis dataKey="day" type="category" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={getHeatmapData()} fill="#10b981" />
          </ScatterChart>
        </ResponsiveContainer>
        <p className="text-sm text-gray-500 mt-2">Darker areas indicate higher engagement times</p>
      </div>

      {/* A/B Testing Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">A/B Testing Results</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getABTestData()}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="variant" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="openRate" fill="#10b981" />
            <Bar dataKey="clickRate" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Subscriber Lifecycle Stages */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Subscriber Lifecycle Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <Treemap
            data={getLifecycleData()}
            dataKey="count"
            aspectRatio={4 / 3}
            stroke="#fff"
            fill="#10b981"
          >
            <Tooltip />
          </Treemap>
        </ResponsiveContainer>
      </div>

      {/* Email Client Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Email Client Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={getEmailClientData()}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {getEmailClientData().map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Export & Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Export & Actions</h2>
        <div className="flex flex-wrap gap-4">
          <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">
            Export to CSV
          </button>
          <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
            Export to PDF
          </button>
          <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700">
            Schedule Report
          </button>
          <button className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700">
            Share Dashboard
          </button>
        </div>
      </div>
    </>
  )
}
